import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Button, Input } from 'reactstrap';
import { Dispatch } from 'redux';
import { Application, Applications, Server, Server2, User, Users, ApplicationStatus } from '../../clients/aasServerClient.generated';
import { deleteApplications } from '../../stores/applications/actionCreators';
import { getApplicationName } from '../../stores/applications/selectors';
import { getGroupName } from '../../stores/groups/selectors';
import { RootState } from '../../stores/store';
import { select, unselect } from '../../stores/tables/actionCreators';
import { isSelected } from '../../stores/tables/selectors';
import { getUserName } from '../../stores/users/selectors';
import { getWorkspaceName } from '../../stores/workspaces/selectors';
import ConfirmModal from '../common/modals/ConfirmModal';
import StatusIndicator from './StatusIndicator';
import { getMaintenanceMode } from '../../repositories/ApplicationRepository';
import MaintenanceIndicator from './MaintenanceIndicator';

interface Props extends LocalizeContextProps, RouteComponentProps {
    application: Application;
    selected: boolean;
    getUserName: (id: string) => string;
    getWorkspaceName: (id: string) => string;

    select: (id: string) => void;
    unselect: (id: string) => void;
    delete: (id: string) => void;
}

interface State {
    showDelete: boolean;
    maintenanceMode?: boolean;
}

class ApplicationsTableRow extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);
        this.state = {
            showDelete: false,
            maintenanceMode: undefined,
        };
    }

    private static MMdictionary: any = {}

    private updatingMM = false;
    private async updateMainenanceMode() {
        this.updatingMM = true;
        var mode = undefined;
        if (
          this.props.application.maintenanceMode === undefined &&
          ApplicationsTableRow.MMdictionary[this.props.application.id] !==
            undefined
        ) {
          mode = ApplicationsTableRow.MMdictionary[this.props.application.id];
        } else {
          try {
            mode = await getMaintenanceMode(this.props.application.id);
          } catch {
            mode = null;
          }
          ApplicationsTableRow.MMdictionary[this.props.application.id] = mode;
        }
        this.props.application.maintenanceMode = mode;
        this.setState({ maintenanceMode: mode }, () => { this.updatingMM = false;})
    }

    public render() {
        const t = this.props.translate;
        const application = this.props.application;

        if (this.state.maintenanceMode === undefined && application.maintenanceMode !== undefined) {
          this.setState({ maintenanceMode: application.maintenanceMode });
        } else if (!this.updatingMM && application.maintenanceMode === undefined) {
          this.updateMainenanceMode();
        }

          return (
            <tr
              className={classnames(
                'table-row',
                this.props.selected ? 'selected' : ''
              )}
              onClick={e => {
                this.props.selected
                  ? this.props.unselect(application.id)
                  : this.props.select(application.id);
              }}>
              <td>
                <Input
                  type='checkbox'
                  checked={this.props.selected}
                  onChange={() => null}
                />
              </td>
              <td className='icon-td'>
                <StatusIndicator application={application} />
              </td>
              <td>
                {application.status?.status !== undefined
                  ? (application.status?.numberOfQueuedActions ?? 0) +
                    (application.status?.numberOfQueuedExecModelActions ?? 0)
                  : null}
              </td>
              <td onClick={this.updateMainenanceMode.bind(this)}>
                <MaintenanceIndicator
                  maintenanceMode={this.state.maintenanceMode}
                />
              </td>
              <td>{application.title}</td>
              <td>{application.location_path}</td>
              <td>{this.getServerCell(application.server)}</td>
              <td>{application.server_alias}</td>         
              <td style={{ backgroundColor: application.color }}>
                {application.color}
              </td>            
              <td>{this.getUserCell(application.users)}</td>
              <td>{this.getWorkspaceCell(application.workspace!.id)}</td>
              <td className='icon-btn-td'>
                <Button
                  color='primary'
                  onClick={e => {
                    e.stopPropagation();
                    this.props.history.push(
                      '/applications/edit/' + this.props.application.id
                    );
                  }}>
                  <FontAwesomeIcon
                    icon={['fal', 'pencil']}
                    className='noMarginRight'
                  />
                </Button>
              </td>
              <td className='icon-btn-td'>
                <Button
                  color='danger'
                  onClick={e => {
                    e.stopPropagation();
                    this.setState({ showDelete: true });
                  }}>
                  <FontAwesomeIcon
                    icon={['fal', 'trash']}
                    className='noMarginRight'
                  />
                </Button>
                <ConfirmModal
                  header={t('applications.modal.delete.header') as string}
                  confirmText={t('applications.modal.delete.delete') as string}
                  isOpen={this.state.showDelete}
                  buttonColor='danger'
                  cancel={() => this.setState({ showDelete: false })}
                  confirm={() => this.props.delete(this.props.application.id)}>
                  {`${t('applications.modal.delete.text')} ${
                    this.props.application.title || this.props.application.id
                  }`}
                </ConfirmModal>
              </td>
              <td className='icon-btn-td'>
                <Button
                  color='secondary'
                  onClick={e => {
                    e.stopPropagation();
                    this.goToTaskprocessor(application.server);
                  }}>
                  <FontAwesomeIcon
                    icon={['fal', 'clock']}
                    className='noMarginRight'
                  />
                </Button>
              </td>
            </tr>
          );
    }

    private goToTaskprocessor = (server: Server2) => {
        // TODO open actual application. Requires mergin hostname value with TP manager,
        // as the names can be different there.
        window.open(`https://${server!.hostname.replace(/^\.+/g, '')}/tpm`, '_blank');
        // TODO when TP manager can do proper auth and SSL, replace the above line with this
        // commented one. (or disallow unsecure)
        // window.location.assign(`${server!.secure ? 'https://' : 'http://'}${hostname}:5000`);
    }

    private getUserCell = (users?: Users[]) => {
        if (users === undefined || users.length === 0) {
            return '';
        } else if (users.length > 1) {
            return this.props.translate('applications.table.cells.assigned', { number: users.length });
        } else {
            return this.props.getUserName(users[0].id);
        }
    }

    private getServerCell = (server?: Server2) => {
        if (server === undefined) {
            return '';
        } else {
            return server.hostname;
        }
    }

    private getWorkspaceCell = (workspaceId: string) => {
        if (workspaceId === undefined) {
            return '';
        } else {
            return this.props.getWorkspaceName(workspaceId);
        }

    }
}

const mapStateToProps = (state: RootState, props: { application: Application }) => ({
    getApplicationName: getApplicationName(state),
    getUserName: getUserName(state),
    getWorkspaceName: getWorkspaceName(state),
    selected: isSelected(state, 'applications', props.application.id),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    delete: (id: string) => dispatch(deleteApplications([id])),
    select: (id: string) => dispatch(select('applications', id)),
    unselect: (id: string) => dispatch(unselect('applications', id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withLocalize(ApplicationsTableRow)));